import { ChevronLeftIcon, CurrencyDollarIcon } from '@systemeio/icons';
import { CurrencyEnum, PricePlanType, PricePlanTypeEnum } from '@systemeio/types';
import { BadRequest, InternalError } from '@systemeio/util';
import { defaultPricePlanCreateErrors, defaultPricePlanCreateValues, PricePlanFormFields } from 'modules/digital-product/price-plans/components/price-plan-form-fields';
import { usePricePlansApi } from 'modules/digital-product/price-plans/hooks/use-price-plans-api';
import { useUserPricePlans } from 'modules/digital-product/price-plans/hooks/use-user-price-plans';
import { PricePlanCreateErrorsInterface } from 'modules/digital-product/price-plans/types/price-plan-interface';
import { getPricePlanCreatePayload } from 'modules/digital-product/price-plans/utils/get-price-plan-create-payload';
import { IconTile } from 'shared/components/icon-tile';
import React, { useEffect, useState } from 'react';
import { Button, FieldErrorAndDescription, Modal, toast } from 'shared/components';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
interface CreatePricePlanModalProps {
  currency: CurrencyEnum;
  /** Plan type preselected in the attach modal. */
  initialType?: PricePlanTypeEnum;
  onBack: () => void;
  onClose: () => void;
  onCreated: (pricePlan: PricePlanType) => void;
  opened: boolean;
}
export function CreatePricePlanModal({
  currency,
  initialType,
  onBack,
  onClose,
  onCreated,
  opened
}: CreatePricePlanModalProps) {
  const {
    t
  } = useLocoTranslation();
  const {
    createPricePlan
  } = usePricePlansApi();
  const {
    mutateUserPricePlans
  } = useUserPricePlans(currency);
  const [tempState, setTempState] = useState(defaultPricePlanCreateValues);
  const [errors, setErrors] = useState(defaultPricePlanCreateErrors);
  const [commonError, setCommonError] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  useEffect(() => {
    if (opened) {
      setTempState({
        ...defaultPricePlanCreateValues,
        type: initialType ?? PricePlanTypeEnum.one_shot
      });
      setErrors(defaultPricePlanCreateErrors);
      setCommonError('');
    }
  }, [opened, initialType]);
  const isReady = !!tempState.name && !!tempState.innerName && !!tempState.statementDescriptor;
  const handleCreate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isReady) return;
    try {
      setErrors(defaultPricePlanCreateErrors);
      setCommonError('');
      setIsFetching(true);
      const pricePlan = await createPricePlan(getPricePlanCreatePayload(tempState, currency));
      await mutateUserPricePlans(data => data && [...data, pricePlan], false);
      onCreated(pricePlan);
    } catch (e) {
      if (e instanceof BadRequest) {
        if (e.errors.fields) {
          const fields = e.errors.fields as unknown as PricePlanCreateErrorsInterface;
          setErrors(prev => ({
            ...prev,
            ...fields
          }));
        }
        if (e.errors.common) {
          setCommonError(e.errors.common.join(''));
        }
      } else if (e instanceof InternalError) {
        setCommonError(t('core.error.internal_error_message'));
      } else {
        toast.error(t('global.error'));
      }
    } finally {
      setIsFetching(false);
    }
  };
  return <Modal opened={opened} onClose={onClose} isFetching={isFetching} title={<div className={'flex items-center gap-4'}>
          <Button aria-label={t('global.back')} disabled={isFetching} onClick={onBack} variant={'gray'} size={'icon-sm'}>
            <ChevronLeftIcon />
          </Button>
          <IconTile size={'m'} tone="gray">
            <CurrencyDollarIcon />
          </IconTile>
          <div className={'flex flex-col'}>
            <span className={'text-base font-medium'}>
              {t('dashboard.digital_product.price_plans.header.new')}
            </span>
            <span className={'text-text-tertiary text-sm font-normal'}>
              {t('dashboard.digital_product.price_plans.header.new-description')}
            </span>
          </div>
        </div>} render={<form onSubmit={handleCreate} />} footer={<>
          <Button type="button" className="tablet:flex-none flex-1" disabled={isFetching} onClick={onClose} variant="secondary" size="md">
            {t('global.cancel')}
          </Button>
          <Button disabled={!isReady} isLoading={isFetching} className="tablet:flex-none flex-1" type="submit" variant="primary" size="md">
            {t('global.create')}
          </Button>
        </>}>
      <div className={'flex flex-col gap-4'}>
        <PricePlanFormFields currency={currency} errors={errors} value={tempState} onChange={setTempState} />
        <div className={'text-text-primary flex flex-wrap items-center gap-2'}>
          <span>
            {t('dashboard.funnel.configuration.common.price_plan.create_price_plan_footer')}
          </span>
          <Button type="button" variant="link" disabled={isFetching} onClick={onBack}>
            {t('dashboard.funnel.configuration.common.price_plan.create_price_plan_footer_link')}
          </Button>
        </div>
        <FieldErrorAndDescription error={commonError} errorClassName="text-center" />
      </div>
    </Modal>;
}